<template>
    <pc-input-group>
        <div class="avoid-page-break-inside">
            <pc-label
                v-bind:label="label"
                v-bind:show-required="required"
                v-bind:show-help="showHelp"
                v-bind:help-link="showHelpLinkOnly"
                class="avoid-page-break-after"
                v-on:help-clicked="emitHelpClicked" />
            <div class="avoid-page-break-after">
                <slot name="help" />
            </div>
            <div v-if="nextInverterFilteringNote">
                <pc-alert
                    v-bind:message="nextInverterFilteringNote"
                    severity="warning"
                    shown />
            </div>
            <div
                v-bind:class="responsiveClasses.systemWrapper"
                class="mt-3">
                <transition-group
                    tag="span"
                    v-bind:css="false"
                    v-on:enter="enter"
                    v-on:leave="leave">
                    <subsystem
                        v-for="subsystem in subsystems"
                        v-bind:id="subsystem.id"
                        v-bind:ref="subsystem.id"
                        v-bind:key="subsystem.id"
                        v-bind:element="element"
                        v-bind:subsystem="subsystem"
                        v-bind:readonly="readonly"
                        v-on:copy-subsystem="copySubsystem"
                        v-on:copy-inverter="copyInverter" />
                </transition-group>
                <pc-button
                    v-bind:disabled="readonly"
                    data-test-role="add-inverter-btn"
                    v-on:click="addAnotherSubsystem">
                    {{ addInverterButtonText }}
                </pc-button>
            </div>
        </div>
        <hr />
        <div class="avoid-page-break-inside">
            <div v-if="showLocationConfigurationWarnings">
                <small class="text-body-secondary">{{ zipCodeNotConfiguredBlockCalculationText }}</small>
            </div>
            <div class="row gx-3">
                <div v-bind:class="responsiveClasses.outputWrapper">
                    <div
                        v-for="output in systemOutputs"
                        v-bind:key="output.label"
                        v-bind:class="responsiveClasses.outputRowWrapper"
                        class="row">
                        <div
                            v-bind:class="responsiveClasses.outputRow"
                            class="fw-bold">
                            {{ output.label }}
                        </div>
                        <div v-bind:class="responsiveClasses.outputRow">
                            <template v-if="hasCalculatedOutput">
                                <span
                                    v-for="(value, index) in output.values"
                                    v-bind:key="index"
                                    v-bind:data-test-role="value.testRole">
                                    <template v-if="index > 0"> / </template>
                                    {{ value.value }}
                                    {{ value.unit }}
                                </span>
                            </template>
                            <template v-else>
                                <span
                                    v-if="isDirty"
                                    class="fw-bold text-danger"
                                    data-test-role="needs-calculated-message">
                                    {{ needsRecalculatedMessage }}
                                </span>
                                <span
                                    v-else
                                    data-test-role="needs-calculated-message">
                                    {{ notYetCalculatedMessage }}
                                </span>
                            </template>
                        </div>
                    </div>
                </div>
                <div
                    v-bind:class="responsiveClasses.calculateRow"
                    class="col d-flex align-items-start">
                    <pc-loader
                        v-bind:loading="calculating"
                        data-test-role="calculate-btn-loader"
                        small>
                        <pc-button
                            v-bind:disabled="showLocationConfigurationWarnings || readonly"
                            data-test-role="calculate-btn"
                            data-gtmid="EditProject-CalculatePvSystem"
                            v-on:click="calculateOutput">
                            {{ calculateButtonText }}
                        </pc-button>
                    </pc-loader>
                </div>
                <div
                    v-if="showMonthlyOutput"
                    class="order-3 col-12 mt-3">
                    <div class="fw-bold">
                        {{ monthlyEstimatedProductionLabel }}
                    </div>
                    <div class="row gx-3">
                        <div
                            v-for="(production, index) in monthlyProduction"
                            v-bind:key="index"
                            v-bind:class="responsiveClasses.monthlyOutput"
                            class="px-1 d-flex flex-column align-items-center">
                            <div class="fw-bold">
                                {{ production.label }}
                            </div>
                            <div>{{ production.value }}</div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="d-flex flex-column align-items-center">
                <transition
                    v-bind:css="false"
                    v-on:enter="enter"
                    v-on:leave="leave">
                    <div
                        v-if="outputError"
                        style="max-width: 100%"
                        class="fw-bold text-danger">
                        {{ outputError }}
                    </div>
                </transition>
            </div>
        </div>
        <hr />
    </pc-input-group>
</template>

<script>
    import base, { mapGroupIdsAndValues } from "./_elementBase";
    import {
        pvSystemController,
        SUFFIXES,
        MONTHLY_OUTPUT_SEPARATOR
    } from "Forms/Components/ProjectForm/ProjectElements/pvSystemElementController";
    import { PROJECT_FORM_TYPES } from "Forms/Components/ProjectForm/projectFormStore";
    import { FORM_TYPES } from "SharedStores/Form/formStore";
    import { PV_LOCATION_TYPE, PV_DEPENDENCY_LABELS, PV_SYSTEM_FIELDS } from "Forms/formConstants";
    import { ELEMENT_PROPERTY_NAME } from "Types/elementTypes";
    import subsystem from "./pvsystem/subsystem.vue";
    import { SLIDE_DOWN_UP } from "Util/animations";
    import { mapI18n } from "Util/i18n";
    import { KEYS, NAMESPACE } from "./i18n/pvsystem.i18n";
    import { ValidationHub } from "Validation/validationHub";
    import { getResponsiveClassMap } from "Responsive/manager";

    const responsiveConfig = {
        systemWrapper: {
            // ms-2 ms-sm-5
            ms: { xs: 2, sm: 5 }
        },
        outputWrapper: {
            // order-2 order-md-1 col-12 col-md-8 col-lg-6
            order: { xs: 2, md: 1 },
            col: { xs: 12, md: 8, lg: 6 }
        },
        outputRowWrapper: {
            // mb-2 mb-sm-0
            mb: { xs: 2, sm: 0 }
        },
        outputRow: {
            // col-12 col-sm-6
            col: { xs: 12, sm: 6 }
        },
        calculateRow: {
            // order-1 order-md-2 justify-content-md-around
            order: { xs: 1, md: 2 },
            "justify-content": { md: "around" }
        },
        monthlyOutput: {
            // col-3 col-sm-2 col-md-1
            col: { xs: 3, sm: 2, md: 1 }
        }
    };

    export default {
        components: { subsystem },
        extends: base,
        inject: {
            viewport: {
                from: "viewport",
                default: null
            }
        },
        provide() {
            return {
                pvSystemElement: this,
                validationHub: this.validationHub
            };
        },
        data() {
            return {
                calculating: false,
                outputError: null,
                validationEnabled: true,
                validationHub: new ValidationHub()
            };
        },
        computed: {
            estimationLocationType() {
                return Number(this.element.Properties[ELEMENT_PROPERTY_NAME.LocationType] ?? PV_LOCATION_TYPE.ZipCode);
            },
            isZipCodeLocation() {
                return this.estimationLocationType === PV_LOCATION_TYPE.ZipCode;
            },
            isReferenceLocation() {
                return this.estimationLocationType === PV_LOCATION_TYPE.Reference;
            },
            isLocationDependencyDefined() {
                return this.element.DependencyDefinitions && this.element.DependencyDefinitions.length > 0;
            },
            showLocationConfigurationWarnings() {
                return !this.isLocationDependencyDefined && !this.isReferenceLocation;
            },
            // This is the DependencyDefinition for the single Location (sub-)Element for the PV System, if any
            singleLatLonDependency() {
                let latLonDependency = null;
                if (this.isLocationDependencyDefined) {
                    latLonDependency = this.element.DependencyDefinitions.find(
                        (dd) => dd.Label === PV_DEPENDENCY_LABELS.Geolocation
                    );
                }
                return latLonDependency;
            },
            // This is the FormElement that is or contains the Location (sub-) Element for the PV System, if any;
            //  if a Location (sub-)Element is not used or the FormElement is not on the current form, null is returned.
            locationFormElement() {
                let locFormElement = null;
                if (this.singleLatLonDependency) {
                    const formElements = this.$store.getters[FORM_TYPES.getters.orderedFormElements];
                    locFormElement = formElements.find(
                        (fe) =>
                            fe.DataFieldId === this.singleLatLonDependency.DataFieldId ||
                            fe.Properties[ELEMENT_PROPERTY_NAME.LocationSubElementId] ===
                                this.singleLatLonDependency.DataFieldId
                    );
                }
                return locFormElement;
            },
            // This is the ProjectElement for the FormElement that is or contains the Location (sub-) Element for the
            //  PV System, if any; if a Location (sub-)Element is not used or the FormElement is not on the current form,
            //  null is returned.
            locationProjectElement() {
                let locProjectElement = null;
                if (this.locationFormElement && !this.isInFormEditor) {
                    // The element containing the location is on the form; check for validation errors
                    locProjectElement = this.$store.getters[PROJECT_FORM_TYPES.getters.projectElementFromId](
                        this.locationFormElement.PublicId
                    );
                }
                return locProjectElement;
            },
            // Returns true if the PV System is dependent on a Location (sub-)Element that is on the form and has validation errors,
            //  false otherwise
            locationHasValidationErrors() {
                return (
                    this.locationProjectElement &&
                    this.locationProjectElement.auxState &&
                    this.locationProjectElement.auxState.hasLocationValidationErrors
                );
            },
            nextInverterFilteringNote() {
                let note = "";
                if (this.element && this.element.AuxiliaryModel) {
                    if (this.element.AuxiliaryModel.hasOwnProperty("nextInverterFilteringNote")) {
                        if (this.element.AuxiliaryModel.nextInverterFilteringNote) {
                            note = this.element.AuxiliaryModel.nextInverterFilteringNote;
                        }
                    }
                }
                return note;
            },
            subsystems() {
                if (this.isProjectFormModuleRegistered()) {
                    return pvSystemController.computeSubsystems(this.element, this.$store.getters);
                }
                return [];
            },
            isMonthlyShading() {
                return pvSystemController.isMonthlyShading(this.element);
            },
            showMonthlyOutput() {
                if (this.$serviceControllers.PvSystem) {
                    return this.$serviceControllers.PvSystem.DisplayMonthlyOutput;
                }
                return false;
            },
            systemOutputs() {
                const pvData = this.$serviceControllers.PvSystem;
                const outputs = [];
                if (!pvData) {
                    return outputs;
                }

                const systemRating = {
                    label: this.systemRatingLabel,
                    values: [{ value: this.NameplateRating, unit: "kW DC", testRole: "dc-rating-output" }]
                };
                if (!pvData.HideCecAcRating) {
                    systemRating.values.push({
                        value: this.CECRating,
                        unit: "kW CEC-AC",
                        testRole: "cec-rating-output"
                    });
                }
                outputs.push(systemRating);

                if (pvData.DisplayCsiRating) {
                    outputs.push({
                        label: this.csiRatingLabel,
                        values: [{ value: this.CSIRating, unit: "kW CSI-AC", testRole: "csi-rating-output" }]
                    });
                }

                if (pvData.DisplayInverterNameplateRating) {
                    outputs.push({
                        label: this.inverterRatingLabel,
                        values: [
                            {
                                value: this.InverterNameplateRating,
                                unit: "kW AC",
                                testRole: "inv-nameplate-rating-output"
                            }
                        ]
                    });
                }
                if (pvData.DisplayEstimatedProduction) {
                    outputs.push({
                        label: this.estimatedAnnualProductionLabel,
                        values: [{ value: this.EstimatedProduction, unit: "kWh", testRole: "productionOutput" }]
                    });
                }
                if (pvData.DisplayDesignFactor) {
                    outputs.push({
                        label: this.designFactorLabel,
                        values: [{ value: this.DesignFactor, unit: "%", testRole: "design-factor-output" }]
                    });
                }
                if (pvData.DisplayAverageInverterEfficiency) {
                    outputs.push({
                        label: this.averageInverterEfficiencyLabel,
                        values: [
                            {
                                value: this.AverageInverterEfficiency,
                                unit: "",
                                testRole: "average-inverter-output"
                            }
                        ]
                    });
                }
                if (pvData.DisplayPtcModuleRating) {
                    outputs.push({
                        label: this.ptcModuleRatingLabel,
                        values: [{ value: this.PTCModuleRating, unit: "kW", testRole: "ptc-module-output" }]
                    });
                }

                if (this.element.AuxiliaryModel.elementShowsBatteryComponent && pvData.EquipmentCalculations) {
                    pvData.EquipmentCalculations.forEach((calc) => {
                        const testRole = calc.FieldLabel.trim().replaceAll(" ", "-").toLowerCase() + "-output";
                        outputs.push({
                            label: this.getCalculatedFieldLabel(calc) + ":",
                            values: [
                                {
                                    value: this[calc.FieldName],
                                    unit: calc.Units,
                                    testRole: testRole
                                }
                            ]
                        });
                    });
                }

                outputs.forEach((out) => {
                    out.values = out.values.filter((v) => Boolean(v.value));
                });

                return outputs;
            },
            isDirty() {
                return this.projectElement && this.projectElement.auxState.isDirty;
            },
            hasCalculatedOutput() {
                return Boolean(this.EstimatedProduction) && this.EstimatedProduction >= 0;
            },
            monthlyProduction() {
                const monthlyProduction =
                    (this.MonthlyEstimatedProduction &&
                        this.MonthlyEstimatedProduction.split(MONTHLY_OUTPUT_SEPARATOR)) ||
                    [];
                // eslint-disable-next-line max-len
                return this.$app.DateCultureMessageInfo.AbbreviatedMonthsOfTheYear.filter((v) => Boolean(v)).map(
                    (label, index) => ({
                        label,
                        value: monthlyProduction[index] || ""
                    })
                );
            },
            readonly() {
                return this.baseReadonly || this.isPrefill;
            },
            ...mapGroupIdsAndValues(SUFFIXES.outputFields),
            responsiveClasses() {
                return getResponsiveClassMap(this.viewport, responsiveConfig);
            },
            ...mapI18n(
                {
                    ...KEYS,
                    invalidZipCodeErrorMessage() {
                        return {
                            key: KEYS.invalidZipCodeErrorMessage,
                            values: {
                                zipFieldDescriptor: this.element.AuxiliaryModel.zipFieldDescriptor
                            }
                        };
                    },
                    invalidLocationErrorMessage() {
                        return {
                            key: KEYS.invalidLocationErrorMessage,
                            values: {
                                locationFieldDescriptor: this.element.AuxiliaryModel.locationFieldDescriptor
                            }
                        };
                    }
                },
                NAMESPACE
            )
        },
        watch: {
            locationHasValidationErrors() {
                if (!this.locationHasValidationErrors && this.outputError === this.invalidLocationErrorMessage) {
                    this.outputError = null;
                }
            }
        },
        created() {
            // If there are no subsystems, add an empty one
            if (!this.isAdmin && !this.readonly && this.subsystems.length <= 0) {
                this.addEmptySubsystem(true);
            }
        },
        methods: {
            async clearOutputValues() {
                await pvSystemController.clearOutputValues(this.element, this.$store.dispatch);
            },
            async calculateOutput() {
                this.outputError = null;
                this.validationHub.validate();

                if (this.isProjectFormModuleRegistered()) {
                    if (!this.estimationPrerequisitesSatisfied()) {
                        this.clearOutputValues();
                        this.outputError = this.isZipCodeLocation
                            ? this.invalidZipCodeErrorMessage
                            : this.invalidLocationErrorMessage;
                        return;
                    }

                    try {
                        this.calculating = true;
                        const result = await this.$store.dispatch(PROJECT_FORM_TYPES.actions.calcPvOutput, {
                            elementId: this.element.PublicId
                        });
                        if (result.hasError) {
                            this.outputError = result.error;
                        }
                    } finally {
                        this.calculating = false;
                    }
                }
            },
            estimationPrerequisitesSatisfied() {
                let latField = "";
                let longField = "";
                let zipDependency;
                switch (this.estimationLocationType) {
                    case PV_LOCATION_TYPE.Reference:
                        return true;
                    case PV_LOCATION_TYPE.Geocoordinate:
                        if (!this.isLocationDependencyDefined) {
                            return false;
                        }
                        // First check for single Location dependency
                        if (this.singleLatLonDependency) {
                            if (this.locationFormElement) {
                                // The element containing the location is on the form; check for validation errors
                                return !this.locationHasValidationErrors;
                            } else {
                                // The element containing the location is not on the form; assume any value is valid
                                return this.getProjectValue(this.singleLatLonDependency.DataFieldId);
                            }
                        }
                        // Then check for obsolete two-field dependency
                        for (const dependencyDefinition of this.element.DependencyDefinitions) {
                            if (dependencyDefinition.Label === PV_DEPENDENCY_LABELS.Latitude) {
                                latField = dependencyDefinition.DataFieldId;
                            } else if (dependencyDefinition.Label === PV_DEPENDENCY_LABELS.Longitude) {
                                longField = dependencyDefinition.DataFieldId;
                            }
                        }
                        return (
                            latField && longField && this.getProjectValue(latField) && this.getProjectValue(longField)
                        );
                    default: // PV_LOCATION_TYPE.ZipCode:
                        if (!this.isLocationDependencyDefined) {
                            return null;
                        }
                        zipDependency = this.element.DependencyDefinitions.find(
                            (dd) => dd.Label === PV_DEPENDENCY_LABELS.ZipCode
                        );
                        if (!zipDependency) {
                            return null;
                        }
                        return this.getProjectValue(zipDependency.DataFieldId);
                }
            },
            setPvConfig(subsystems, systemIsNew) {
                this.outputError = null;
                if (this.isProjectFormModuleRegistered()) {
                    this.$store.dispatch(PROJECT_FORM_TYPES.actions.setProjectPvConfig, {
                        elementId: this.element.PublicId,
                        pvConfig: subsystems,
                        systemIsNew: systemIsNew
                    });
                }
            },
            cloneSubsystems() {
                return JSON.parse(JSON.stringify(this.subsystems));
            },
            setInverterProperty({ subsystemId, name, value }) {
                const clone = this.cloneSubsystems();
                const subsystem = this.findSubsystem(clone, subsystemId);
                if (subsystem) {
                    subsystem[name] = value;
                }
                this.setPvConfig(clone, false);
            },
            setPvModuleProperty({ subsystemId, pvModuleId, name, value }) {
                const clone = this.cloneSubsystems();
                const pvModule = this.findPvModule(clone, subsystemId, pvModuleId);
                if (pvModule) {
                    pvModule[name] = value;
                }
                this.setPvConfig(clone, false);
            },
            setPvModuleShading({ subsystemId, pvModuleId, index, value }) {
                const clone = this.cloneSubsystems();
                const pvModule = this.findPvModule(clone, subsystemId, pvModuleId);
                if (pvModule && pvModule.shading.length > index) {
                    pvModule.shading[index] = value;
                }
                this.setPvConfig(clone, false);
            },
            setEquipmentItemProperty({ subsystemId, equipmentItemId, name, value }) {
                const clone = this.cloneSubsystems();
                const equipmentItem = this.findEquipmentItem(clone, subsystemId, equipmentItemId);
                if (equipmentItem) {
                    equipmentItem[name] = value;
                }
                this.setPvConfig(clone, false);
            },
            getCalculatedFieldLabel(data) {
                let label = "";
                switch (data.FieldName) {
                    case PV_SYSTEM_FIELDS.TotalNameplateEnergyCapacity:
                        label = this.totalNameplateEnergyCapacityLabel;
                        break;
                    default:
                        label = data.FieldLabel;
                        break;
                }
                return label;
            },
            addAnotherSubsystem() {
                this.addEmptySubsystem(false);
            },
            addEmptySubsystem(systemIsNew) {
                const clone = this.cloneSubsystems();
                clone.push(pvSystemController.getEmptySubsystem(this.element));
                this.setPvConfig(clone, systemIsNew);
            },
            addEmptyModule(subsystemId) {
                const clone = this.cloneSubsystems();
                const index = clone.findIndex((system) => system.id === subsystemId);
                const subsystem = clone[index];
                if (subsystem) {
                    const pvModule = pvSystemController.getEmptyModule(this.element);
                    pvModule.assignedInverter = index;
                    subsystem.moduleGroups.push(pvModule);
                }
                this.setPvConfig(clone, false);
            },
            addEmptyEquipmentItem(subsystemId, equipmentType) {
                const clone = this.cloneSubsystems();
                const index = clone.findIndex((system) => system.id === subsystemId);
                const subsystem = clone[index];
                if (subsystem) {
                    const equipmentItem = pvSystemController.getEmptyEquipmentItem(this.element, equipmentType);
                    equipmentItem.assignedInverter = index;
                    subsystem.equipmentGroups.push(equipmentItem);
                }
                this.setPvConfig(clone, false);
            },
            deleteSubsystem(subsystemId) {
                if (this.subsystems.length <= 1) {
                    return;
                }
                const clone = this.cloneSubsystems();
                const index = clone.findIndex((system) => system.id === subsystemId);
                clone.splice(index, 1);
                this.setPvConfig(clone, false);
            },
            deleteModule({ subsystemId, pvModuleId }) {
                const clone = this.cloneSubsystems();
                const subsystem = this.findSubsystem(clone, subsystemId);
                if (subsystem) {
                    const moduleIndex = subsystem.moduleGroups.findIndex((pvModule) => pvModule.id === pvModuleId);
                    subsystem.moduleGroups.splice(moduleIndex, 1);
                }
                this.setPvConfig(clone, false);
            },
            deleteEquipmentItem({ subsystemId, equipmentItemId }) {
                const clone = this.cloneSubsystems();
                const subsystem = this.findSubsystem(clone, subsystemId);
                if (subsystem) {
                    const equipmentItemIndex = subsystem.equipmentGroups.findIndex(
                        (equipmentItem) => equipmentItem.id === equipmentItemId
                    );
                    subsystem.equipmentGroups.splice(equipmentItemIndex, 1);
                }
                this.setPvConfig(clone, false);
            },
            findSubsystem(subsystems, subsystemId) {
                return subsystems.find((subsystem) => subsystem.id === subsystemId);
            },
            findPvModule(subsystems, subsystemId, pvModuleId) {
                const subsystem = this.findSubsystem(subsystems, subsystemId);
                if (subsystem) {
                    return subsystem.moduleGroups.find((pvModule) => pvModule.id === pvModuleId);
                }
                return null;
            },
            findEquipmentItem(subsystems, subsystemId, equipmentItemId) {
                const subsystem = this.findSubsystem(subsystems, subsystemId);
                if (subsystem) {
                    return subsystem.equipmentGroups.find((equipmentItem) => equipmentItem.id === equipmentItemId);
                }
                return null;
            },
            // Copies the entire subsystem: the inverter and its module and battery children
            async copySubsystem(subsystemId, canAddStorage, inverterIsEssModel) {
                const clone = this.cloneSubsystems();
                const subsystemIndex = clone.findIndex((subsystem) => subsystem.id === subsystemId);
                if (subsystemIndex !== -1) {
                    const subsystem = clone[subsystemIndex];
                    const newSubsystem = pvSystemController.getEmptySubsystem(this.element);
                    // Only copy the inverter model if adding storage equipment is permitted or the inverter is not an ESS model
                    if (canAddStorage || !inverterIsEssModel) {
                        newSubsystem.manufacturerId = subsystem.manufacturerId;
                        newSubsystem.modelId = subsystem.modelId;
                    }
                    // Loop through module groups and copy them to the newSubsystem
                    let index = 0;
                    subsystem.moduleGroups.forEach((pvModule) => {
                        // getEmptySubsystem creates one empty moduleGroup, so use it for the first module
                        const newPvModule =
                            index === 0
                                ? newSubsystem.moduleGroups[0]
                                : pvSystemController.getEmptyModule(this.element);
                        newPvModule.assignedInverter = pvModule.assignedInverter + 1;
                        newPvModule.manufacturerId = pvModule.manufacturerId;
                        newPvModule.modelId = pvModule.modelId;
                        newPvModule.index = index;
                        // Append newly created moduleGroups (not the first one that was already there) to the array
                        if (index > 0) {
                            newSubsystem.moduleGroups.push(newPvModule);
                        }
                        index++;
                    });
                    // Only copy equipment groups if adding storage equipment is permitted (don't copy an invalid battery group)
                    if (canAddStorage) {
                        // Loop through equipment (battery) groups and copy them to the newSubsystem
                        index = 0;
                        subsystem.equipmentGroups.forEach((equipmentItem) => {
                            // getEmptySubsystem does not create an empty equipmentGroup, so create one for each equipmentItem
                            const newEquipmentItem = pvSystemController.getEmptyEquipmentItem(
                                this.element,
                                equipmentItem.equipmentType
                            );
                            newEquipmentItem.assignedInverter = equipmentItem.assignedInverter + 1;
                            newEquipmentItem.manufacturerId = equipmentItem.manufacturerId;
                            newEquipmentItem.modelId = equipmentItem.modelId;
                            newEquipmentItem.index = index;
                            newSubsystem.equipmentGroups.push(newEquipmentItem);
                            index++;
                        });
                    }
                    // Insert newSubsystem immediately after subsystem
                    clone.splice(subsystemIndex + 1, 0, newSubsystem);
                    // Update index property of each subsystem
                    clone.forEach((subsystem) => {
                        subsystem.index = clone.indexOf(subsystem);
                    });
                    this.setPvConfig(clone, false);
                    // Put in a break to allow the UI to update with the ref for the new subsystem
                    await this.$app.timeout(0);
                    // Set the focus to the new subsystem
                    if (this.$refs[newSubsystem.id]) {
                        this.$refs[newSubsystem.id][0].focusSubsystemInput();
                    }
                }
            },
            async copyInverter(subsystemId) {
                const clone = this.cloneSubsystems();
                const subsystemIndex = clone.findIndex((subsystem) => subsystem.id === subsystemId);
                if (subsystemIndex !== -1) {
                    const subsystem = clone[subsystemIndex];
                    const newSubsystem = pvSystemController.getEmptySubsystem(this.element);
                    newSubsystem.manufacturerId = subsystem.manufacturerId;
                    newSubsystem.modelId = subsystem.modelId;
                    // Insert newSubsystem immediately after subsystem
                    clone.splice(subsystemIndex + 1, 0, newSubsystem);
                    // Update index property of each subsystem
                    clone.forEach((subsystem) => {
                        subsystem.index = clone.indexOf(subsystem);
                    });
                    this.setPvConfig(clone, false);
                    // Put in a break to allow the UI to update with the ref for the new subsystem
                    await this.$app.timeout(0);
                    // Set the focus to the new subsystem
                    if (this.$refs[newSubsystem.id]) {
                        this.$refs[newSubsystem.id][0].focusSubsystemInput();
                    }
                }
            },
            async copyModuleGroup(subsystemId, pvModuleId) {
                const clone = this.cloneSubsystems();
                const subsystemIndex = clone.findIndex((system) => system.id === subsystemId);
                if (subsystemIndex !== -1) {
                    const subsystem = clone[subsystemIndex];
                    if (subsystem) {
                        const moduleIndex = subsystem.moduleGroups.findIndex((pvModule) => pvModule.id === pvModuleId);
                        if (moduleIndex !== -1) {
                            const pvModule = subsystem.moduleGroups[moduleIndex];
                            const newPvModule = pvSystemController.getEmptyModule(this.element);
                            newPvModule.assignedInverter = subsystemIndex;
                            newPvModule.manufacturerId = pvModule.manufacturerId;
                            newPvModule.modelId = pvModule.modelId;
                            // Insert newPvModule immediately after pvModule
                            subsystem.moduleGroups.splice(moduleIndex + 1, 0, newPvModule);
                            // Update index property of each module
                            subsystem.moduleGroups.forEach((pvModule) => {
                                pvModule.index = subsystem.moduleGroups.indexOf(pvModule);
                            });
                            this.setPvConfig(clone, false);
                            return newPvModule.id;
                        }
                    }
                }
            },
            async copyEquipmentGroup(subsystemId, equipmentId) {
                const clone = this.cloneSubsystems();
                const subsystemIndex = clone.findIndex((system) => system.id === subsystemId);
                if (subsystemIndex !== -1) {
                    const subsystem = clone[subsystemIndex];
                    if (subsystem) {
                        const equipmentIndex = subsystem.equipmentGroups.findIndex(
                            (equipmentItem) => equipmentItem.id === equipmentId
                        );
                        if (equipmentIndex !== -1) {
                            const equipmentItem = subsystem.equipmentGroups[equipmentIndex];
                            const newEquipmentItem = pvSystemController.getEmptyEquipmentItem(
                                this.element,
                                equipmentItem.equipmentType
                            );
                            newEquipmentItem.assignedInverter = subsystemIndex;
                            newEquipmentItem.manufacturerId = equipmentItem.manufacturerId;
                            newEquipmentItem.modelId = equipmentItem.modelId;
                            // Insert newEquipmentItem immediately after equipmentItem
                            subsystem.equipmentGroups.splice(equipmentIndex + 1, 0, newEquipmentItem);
                            // Update index property of each equipmentItem
                            subsystem.equipmentGroups.forEach((equipmentItem) => {
                                equipmentItem.index = subsystem.equipmentGroups.indexOf(equipmentItem);
                            });
                            this.setPvConfig(clone, false);
                            return newEquipmentItem.id;
                        }
                    }
                }
            },
            enter: SLIDE_DOWN_UP.enter,
            leave: SLIDE_DOWN_UP.leave
        }
    };
</script>
